import { useSearchParams } from 'react-router-dom';
import { Alert } from '../components/ui';
import { IconMicrosoft, IconCheckCircle } from '../components/icons';
import { NhcLogo } from '../components/NhcLogo';
import microtechMark from '../assets/microtech-cube-circuit.png';

const POINTS = [
  'Raise enrolment and offboarding requests in one place',
  'Approve and sign off from anywhere, on any device',
  'Track exactly where a request has got to',
];

/**
 * PCB-style traces and a couple of cable runs, in the same idiom as the real
 * Micro Tech mark's own artwork (a circuit pattern radiating off the cube).
 * Static once drawn: motion here runs once on arrival like everywhere else in
 * the app, tracing in rather than looping, so it reads as "powering up" once
 * and then gets out of the way of the text sitting on top of it.
 */
function CircuitTraces() {
  return (
    <svg
      className="pointer-events-none absolute inset-0 h-full w-full text-brand-400/50"
      viewBox="0 0 400 800"
      preserveAspectRatio="xMidYMid slice"
      aria-hidden="true"
    >
      <g fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
        <path className="circuit-line" pathLength="1" style={{ animationDelay: '80ms' }} d="M0 74 H92 V150 H196" />
        <path className="circuit-line" pathLength="1" style={{ animationDelay: '260ms' }} d="M0 168 H48" />
        <path
          className="circuit-line"
          pathLength="1"
          style={{ animationDelay: '160ms' }}
          d="M400 108 H318 V206 H250"
        />
        <path className="circuit-line" pathLength="1" style={{ animationDelay: '340ms' }} d="M400 240 H360" />
      </g>
      {/* The two cable runs: longer, looser curves rather than right angles,
          crossing wide open stretches of the panel a grid of traces alone
          would leave bare. */}
      <g fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="text-copper-400/40">
        <path
          className="circuit-line"
          pathLength="1"
          style={{ animationDelay: '120ms' }}
          d="M-10 320 C 90 300, 140 380, 260 350 S 420 300, 460 340"
        />
        <path
          className="circuit-line"
          pathLength="1"
          style={{ animationDelay: '460ms' }}
          d="M-10 470 C 60 500, 120 440, 220 470 S 360 520, 410 480"
        />
      </g>
      <g fill="currentColor" className="text-brand-300/60">
        {[
          [92, 74],
          [196, 150],
          [318, 108],
          [250, 206],
        ].map(([cx, cy]) => (
          <circle key={`${cx}-${cy}`} className="circuit-node" cx={cx} cy={cy} r="3.5" />
        ))}
      </g>
    </svg>
  );
}

/**
 * A quiet dot grid behind the sign-in card: the light panel had nothing of
 * its own texture-wise, all the "tech" idiom lived on the dark side. Low
 * enough opacity, and in currentColor, that it reads correctly in both
 * themes without a separate dark: variant.
 */
function DotGrid() {
  return (
    <div
      aria-hidden="true"
      className="pointer-events-none absolute inset-0 text-slate-900/[0.05] dark:text-slate-100/[0.06]"
      style={{ backgroundImage: 'radial-gradient(currentColor 1px, transparent 1px)', backgroundSize: '24px 24px' }}
    />
  );
}

export function SignIn() {
  const [params] = useSearchParams();
  const error = params.get('error');

  return (
    <div className="grid min-h-full lg:grid-cols-[1.1fr_1fr]">
      {/* Brand panel */}
      <div className="bg-ore relative hidden flex-col justify-between overflow-hidden bg-brand-900 p-12 lg:flex">
        {/* The panel is dark in both themes, so it always takes the dark cut. */}
        <CircuitTraces />

        <NhcLogo alt="New Horizon Copper" className="relative h-14 w-auto self-start" onDark />

        {/* Centred in the space between the logo and the footer line, rather
            than sitting wherever the flow puts it: on a tall screen that left
            a lot of dead air below the checklist and above the footer. */}
        <div className="relative flex flex-1 items-center">
          <div className="animate-rise max-w-md">
            <p className="text-xs font-semibold tracking-widest text-copper-300 uppercase">
              IT enrolment &amp; offboarding
            </p>
            <h2 className="mt-3 text-3xl leading-tight font-semibold tracking-tight text-white">
              The enrolment form,
              <br />
              without the paper.
            </h2>
            <ul className="stagger mt-8 space-y-3">
              {POINTS.map((point) => (
                <li key={point} className="flex items-start gap-3 text-sm text-brand-100">
                  <IconCheckCircle className="mt-0.5 h-4 w-4 shrink-0 text-copper-300" />
                  {point}
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* The Micro Tech mark now gets its own prominent spot at the top of
            the sign-in panel opposite, so this line stays to what belongs to
            NHC's side of the page: where the organisation actually is. */}
        <p className="relative border-t border-white/10 pt-5 text-xs text-brand-300">
          New Horizon Copper · Kombat, Namibia
        </p>
      </div>

      {/* Sign-in panel */}
      <div className="relative flex items-center justify-center overflow-hidden px-6 py-16">
        <DotGrid />
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -top-24 left-1/2 h-80 w-80 -translate-x-1/2 rounded-full bg-brand-400/10 blur-3xl dark:bg-brand-500/10"
        />

        <div className="animate-rise relative w-full max-w-sm">
          {/* NHC's own mark only needs to show up here on narrow screens: at
              lg the brand panel opposite already carries it. */}
          <div className="mb-6 lg:hidden">
            <NhcLogo alt="New Horizon Copper" className="h-10 w-auto self-start" />
          </div>

          {/* The supplied artwork's wordmark was baked in as black pixels,
              drawn for a light ground: a plate behind it to cope with dark
              mode read as a sticker slapped on. Cropped to just the cube
              and its own circuit arms instead, which hold up on any
              background since the black in them sits on the cube's red,
              never on the page, and the wordmark is set here as real text
              that follows the theme like everything else on the page. */}
          <div
            className="animate-pop mb-8 flex flex-col items-center gap-1 lg:mb-12"
            style={{ animationDelay: '120ms' }}
          >
            <img src={microtechMark} alt="" className="h-20 w-auto lg:h-28" />
            <p className="text-lg font-extrabold tracking-tight text-slate-900 uppercase dark:text-white">
              Micro Tech
            </p>
            <p className="-mt-1 text-[11px] font-semibold tracking-[0.2em] text-slate-500 uppercase dark:text-slate-400">
              Namibia
            </p>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-8 shadow-xl shadow-slate-900/5 dark:border-slate-800 dark:bg-slate-900 dark:shadow-none">
            <p className="text-xs font-semibold tracking-widest text-brand-700 uppercase dark:text-brand-300">
              New Horizon Copper · Enrolment Program
            </p>
            <h1 className="mt-2 text-2xl font-semibold tracking-tight text-slate-900 dark:text-white">
              Sign in
            </h1>
            <p className="mt-2 text-sm leading-relaxed text-slate-600 dark:text-slate-400">
              Use your work Microsoft account to raise, approve and track IT enrolment and offboarding
              requests.
            </p>

            {error && (
              <div className="mt-6">
                <Alert tone="warning">{error}</Alert>
              </div>
            )}

            <a
              href="/api/auth/login"
              className="press mt-8 flex w-full items-center justify-center gap-3 rounded-lg bg-brand-800 px-4 py-3 text-sm font-medium text-white transition-colors hover:bg-brand-700 dark:bg-brand-600 dark:hover:bg-brand-500"
            >
              <IconMicrosoft />
              Sign in with Microsoft
            </a>

            <p className="mt-6 text-xs leading-relaxed text-slate-500 dark:text-slate-400">
              Accounts are created on first sign-in and start with no access. An administrator assigns
              your role before you can do anything.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
